import type { EndPledge, Party } from 'generated/gql';
import { Block, Box, Text } from '@rulebooks/evidend-titan';
import type { SummaryCardsComponentProps } from 'helpers/formatMutation';
import { useLabels } from 'hooks/useLabels';
import { uniqBy } from 'lodash';
import React, { useMemo } from 'react';
import { CapitalCardListItem } from 'summaries/CapitalCards/CapitalCardListItem';
import { ShareholderCapitalCard } from 'summaries/CapitalCards/ShareholderCapitalCard';
import { DeedCard } from 'summaries/DeedCard/DeedCard';

const getEndPledgesOfParty = (endPledges: EndPledge[], party: Party): EndPledge[] =>
  endPledges.filter((endPledge) => endPledge.owner.id === party.id);

export const EndPledgeSummaryCards = ({
  backgroundVariant = 'white',
  mutation,
  onDocumentSave,
}: SummaryCardsComponentProps) => {
  const { register } = mutation;
  const { legalEntity } = register;
  const labels = useLabels('endPledge.summary', legalEntity.isFoundation);
  const endPledges = useMemo(() => mutation.endPledge || [], [mutation.endPledge]);
  const parties = useMemo(
    () =>
      uniqBy(
        endPledges.map((endPledge) => endPledge.owner),
        'id',
      ),
    [endPledges],
  );

  return (
    <>
      {!!parties.length && (
        <>
          <Text color="grey80" spaceAfter={6} type="labelBold">
            {labels.shares}
          </Text>

          <Box spaceAfter={10}>
            {parties.map((party) => (
              <ShareholderCapitalCard
                key={party.id}
                backgroundVariant={backgroundVariant}
                compareDeceaseDate={mutation.effectiveDate}
                party={party}
              >
                {getEndPledgesOfParty(endPledges, party).map((endPledge, index) => (
                  <CapitalCardListItem key={index} shareSeries={endPledge.shareSeries} />
                ))}
              </ShareholderCapitalCard>
            ))}
          </Box>
        </>
      )}

      {mutation.document && (
        <Block spaceAfter={10}>
          <DeedCard
            backgroundVariant={backgroundVariant}
            document={mutation.document}
            mutation={mutation}
            onDocumentSave={onDocumentSave}
          />
        </Block>
      )}
    </>
  );
};
